import React from 'react';

/**
 * The handful of icons the ported marketing/billing screens need.
 *
 * The marketing site pulled these from `lucide-react`; the app has no icon dependency and uses
 * inline SVG everywhere else, so these are hand-inlined at the same 24x24 stroke geometry.
 */

type IconProps = { className?: string };

const base = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

export const CheckIcon: React.FC<IconProps> = ({className}) => (
  <svg {...base} className={className} aria-hidden="true">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

export const ClockIcon: React.FC<IconProps> = ({className}) => (
  <svg {...base} className={className} aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v6l4 2" />
  </svg>
);

export const AlertCircleIcon: React.FC<IconProps> = ({className}) => (
  <svg {...base} className={className} aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 8v4" />
    <path d="M12 16h.01" />
  </svg>
);

export const MailIcon: React.FC<IconProps> = ({className}) => (
  <svg {...base} className={className} aria-hidden="true">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m2 7 10 6 10-6" />
  </svg>
);

export const CreditCardIcon: React.FC<IconProps> = ({className}) => (
  <svg {...base} className={className} aria-hidden="true">
    <rect width="20" height="14" x="2" y="5" rx="2" />
    <path d="M2 10h20" />
  </svg>
);

export const XCircleIcon: React.FC<IconProps> = ({className}) => (
  <svg {...base} className={className} aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <path d="m15 9-6 6" />
    <path d="m9 9 6 6" />
  </svg>
);

export const ExternalLinkIcon: React.FC<IconProps> = ({className}) => (
  <svg {...base} className={className} aria-hidden="true">
    <path d="M15 3h6v6" />
    <path d="M10 14 21 3" />
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
  </svg>
);

export const ZapIcon: React.FC<IconProps> = ({className}) => (
  <svg {...base} className={className} aria-hidden="true">
    <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8Z" />
  </svg>
);

export const FileCodeIcon: React.FC<IconProps> = ({className}) => (
  <svg {...base} className={className} aria-hidden="true">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
    <path d="M14 2v6h6" />
    <path d="m10 13-2 2 2 2" />
    <path d="m14 17 2-2-2-2" />
  </svg>
);

export const FilmIcon: React.FC<IconProps> = ({className}) => (
  <svg {...base} className={className} aria-hidden="true">
    <rect width="18" height="18" x="3" y="3" rx="2" />
    <path d="M7 3v18" />
    <path d="M17 3v18" />
    <path d="M3 12h18" />
  </svg>
);

export const PlayCircleIcon: React.FC<IconProps> = ({className}) => (
  <svg {...base} className={className} aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <path d="m10 8 6 4-6 4Z" />
  </svg>
);

export const ListIcon: React.FC<IconProps> = ({className}) => (
  <svg {...base} className={className} aria-hidden="true">
    <path d="M8 6h13" />
    <path d="M8 12h13" />
    <path d="M8 18h13" />
    <path d="M3 6h.01" />
    <path d="M3 12h.01" />
    <path d="M3 18h.01" />
  </svg>
);

/* ------------------------------------------------------------------------------------------------
 * Workspace icons. The workspace replaced the emoji the desktop app used as buttons — an emoji
 * renders differently on every platform and cannot inherit colour or stroke weight from its state.
 * ---------------------------------------------------------------------------------------------- */

export const UploadCloudIcon: React.FC<IconProps> = ({className}) => (
  <svg {...base} className={className} aria-hidden="true">
    <path d="M12 13v8" />
    <path d="m8 17 4-4 4 4" />
    <path d="M20.88 18.09A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.29" />
  </svg>
);

export const SearchIcon: React.FC<IconProps> = ({className}) => (
  <svg {...base} className={className} aria-hidden="true">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.3-4.3" />
  </svg>
);

export const TrashIcon: React.FC<IconProps> = ({className}) => (
  <svg {...base} className={className} aria-hidden="true">
    <path d="M3 6h18" />
    <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
  </svg>
);

export const DownloadIcon: React.FC<IconProps> = ({className}) => (
  <svg {...base} className={className} aria-hidden="true">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <path d="m7 10 5 5 5-5" />
    <path d="M12 15V3" />
  </svg>
);

export const WandIcon: React.FC<IconProps> = ({className}) => (
  <svg {...base} className={className} aria-hidden="true">
    <path d="m3 21 9-9" />
    <path d="M15 4V2" />
    <path d="M15 10V8" />
    <path d="M12.5 5.5h-2" />
    <path d="M19.5 5.5h-2" />
    <path d="m17 3-1.5 1.5" />
    <path d="M13.5 7 12 8.5" />
    <path d="m17 8-1.5-1.5" />
    <path d="M13.5 4 12 2.5" />
  </svg>
);

export const XIcon: React.FC<IconProps> = ({className}) => (
  <svg {...base} className={className} aria-hidden="true">
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

export const ChevronDownIcon: React.FC<IconProps> = ({className}) => (
  <svg {...base} className={className} aria-hidden="true">
    <path d="m6 9 6 6 6-6" />
  </svg>
);

export const RefreshIcon: React.FC<IconProps> = ({className}) => (
  <svg {...base} className={className} aria-hidden="true">
    <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
    <path d="M21 3v5h-5" />
    <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
    <path d="M3 21v-5h5" />
  </svg>
);

export const AlertTriangleIcon: React.FC<IconProps> = ({className}) => (
  <svg {...base} className={className} aria-hidden="true">
    <path d="m21.7 18-8-14a2 2 0 0 0-3.4 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.7-3" />
    <path d="M12 9v4" />
    <path d="M12 17h.01" />
  </svg>
);

export const FileAudioIcon: React.FC<IconProps> = ({className}) => (
  <svg {...base} className={className} aria-hidden="true">
    <path d="M15 2H6a2 2 0 0 0-2 2v7" />
    <path d="M14 2v4a2 2 0 0 0 2 2h4" />
    <path d="M20 8v12a2 2 0 0 1-2 2h-4" />
    <circle cx="4" cy="18" r="2" />
    <path d="M6 18v-4l4-1v4" />
    <circle cx="8" cy="17" r="2" />
  </svg>
);

export const FileTextIcon: React.FC<IconProps> = ({className}) => (
  <svg {...base} className={className} aria-hidden="true">
    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7z" />
    <path d="M14 2v4a2 2 0 0 0 2 2h4" />
    <path d="M8 13h8" />
    <path d="M8 17h5" />
  </svg>
);

export const FolderIcon: React.FC<IconProps> = ({className}) => (
  <svg {...base} className={className} aria-hidden="true">
    <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />
  </svg>
);

export const PlusIcon: React.FC<IconProps> = ({className}) => (
  <svg {...base} className={className} aria-hidden="true">
    <path d="M5 12h14" />
    <path d="M12 5v14" />
  </svg>
);

export const MinusIcon: React.FC<IconProps> = ({className}) => (
  <svg {...base} className={className} aria-hidden="true">
    <path d="M5 12h14" />
  </svg>
);

/* ------------------------------------------------------------------------------------------------
 * Word list icons. Same reasoning as the workspace set above — these replace the ⭐ 👥 🔒 📚 emoji
 * the list cards used as badges, which is the one place a badge's colour has to mean something.
 * ---------------------------------------------------------------------------------------------- */

export const PencilIcon: React.FC<IconProps> = ({className}) => (
  <svg {...base} className={className} aria-hidden="true">
    <path d="M21.2 6.2a2.8 2.8 0 0 0-4-4L4 15.5 3 21l5.5-1Z" />
    <path d="m15 5 4 4" />
  </svg>
);

export const GlobeIcon: React.FC<IconProps> = ({className}) => (
  <svg {...base} className={className} aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <path d="M2 12h20" />
    <path d="M12 2a15.3 15.3 0 0 1 0 20a15.3 15.3 0 0 1 0-20" />
  </svg>
);

export const LockIcon: React.FC<IconProps> = ({className}) => (
  <svg {...base} className={className} aria-hidden="true">
    <rect width="18" height="11" x="3" y="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

export const EyeOffIcon: React.FC<IconProps> = ({className}) => (
  <svg {...base} className={className} aria-hidden="true">
    <path d="M10.7 5.1A10.9 10.9 0 0 1 12 5c7 0 10 7 10 7a13.2 13.2 0 0 1-1.7 2.7" />
    <path d="M6.6 6.6A13.5 13.5 0 0 0 2 12s3 7 10 7a10.9 10.9 0 0 0 5.4-1.4" />
    <path d="M14.1 14.1a3 3 0 1 1-4.2-4.2" />
    <path d="m2 2 20 20" />
  </svg>
);

export const BookIcon: React.FC<IconProps> = ({className}) => (
  <svg {...base} className={className} aria-hidden="true">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z" />
  </svg>
);

export const UploadIcon: React.FC<IconProps> = ({className}) => (
  <svg {...base} className={className} aria-hidden="true">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <path d="m17 8-5-5-5 5" />
    <path d="M12 3v12" />
  </svg>
);

export const ArrowRightIcon: React.FC<IconProps> = ({className}) => (
  <svg {...base} className={className} aria-hidden="true">
    <path d="M5 12h14" />
    <path d="m12 5 7 7-7 7" />
  </svg>
);

/** `fill-current` on the className fills it — CSS wins over the `fill="none"` presentation attribute. */
export const StarIcon: React.FC<IconProps> = ({className}) => (
  <svg {...base} className={className} aria-hidden="true">
    <path d="M11.5 3.2a.6.6 0 0 1 1 0l2.3 4.7 5.2.7a.6.6 0 0 1 .3 1l-3.8 3.6.9 5.1a.6.6 0 0 1-.9.7L12 16.6l-4.6 2.4a.6.6 0 0 1-.9-.7l.9-5.1-3.8-3.6a.6.6 0 0 1 .3-1l5.2-.7Z" />
  </svg>
);

export const HomeIcon: React.FC<IconProps> = ({className}) => (
  <svg {...base} className={className} aria-hidden="true">
    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <path d="M9 22V12h6v10" />
  </svg>
);

export const LogOutIcon: React.FC<IconProps> = ({className}) => (
  <svg {...base} className={className} aria-hidden="true">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <path d="m16 17 5-5-5-5" />
    <path d="M21 12H9" />
  </svg>
);
